import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Typography, Button } from 'antd';
import {
  ShopOutlined,
  CarOutlined,
  RocketOutlined,
  EyeOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import './ClientDashboard.css';

const { Title, Text } = Typography;

const ClientDashboard = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();

  // Mock client data - in real app, fetch based on clientId
  const clientData = {
    1: {
      name: 'ProShine Auto Detailing',
      tagline: 'Viewing as Marie Service',
      totalLocations: 1,
      totalVehicles: 5,
      activeCampaigns: 2,
      routeMiles: 750,
      impressionsPerMile: 65,
      combinedCTR: '4.5%',
      plan: {
        routeIQ: {
          name: 'RouteIQ',
          description: 'Vehicle & Location Tracking',
          vehicles: 5,
          vehiclePrice: '$49',
          vehicleTotal: '$245',
          locations: 1,
          locationPrice: '$199',
          locationTotal: '$199',
          subtotal: '$444/mo'
        },
        proximityIQ: {
          name: 'ProximityIQ',
          description: 'AI Growth Package',
          smsCredits: '392 / 3,000',
          emailCredits: '1,305 / 3,000',
          basePlatformFee: '$250/mo',
          aiGrowthPackage: '$59/mo',
          monthlyTotal: '$309/mo'
        },
        platformTotal: '$733'
      }
    },
    2: {
      name: 'Blue Ridge HVAC',
      tagline: 'Viewing as James Service',
      totalLocations: 3,
      totalVehicles: 18,
      activeCampaigns: 3,
      routeMiles: 2150,
      impressionsPerMile: 72,
      combinedCTR: '3.8%',
      plan: {
        routeIQ: {
          name: 'RouteIQ',
          description: 'Vehicle & Location Tracking',
          vehicles: 18,
          vehiclePrice: '$49',
          vehicleTotal: '$882',
          locations: 3,
          locationPrice: '$199',
          locationTotal: '$597',
          subtotal: '$1,479/mo'
        },
        proximityIQ: {
          name: 'ProximityIQ',
          description: 'AI Growth Package',
          smsCredits: '1,892 / 5,000',
          emailCredits: '3,421 / 5,000',
          basePlatformFee: '$650/mo',
          aiGrowthPackage: '$77/mo',
          monthlyTotal: '$727/mo'
        },
        platformTotal: '$2,206'
      }
    }
  };

  const client = clientData[clientId] || clientData[1];

  return (
    <div className="client-dashboard">
      {/* Header */}
      <div className="client-dashboard-header">
        <div>
          <div className="admin-badge">
            <Button 
              type="primary" 
              size="small"
              style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
            >
              Admin View Mode
            </Button>
          </div>
          <Title level={2} style={{ margin: '8px 0 0 0' }}>{client.name} Dashboard</Title>
          <Text type="secondary">{client.tagline}</Text>
        </div>
        <div className="last-updated">Last updated: 7:28:42 PM</div>
      </div>

      {/* Enterprise Overview */}
      <div className="enterprise-overview-section">
        <Title level={3}>Enterprise Overview</Title>
        <Text type="secondary">Monitor performance across all your locations and campaigns</Text>

        <Card className="locations-overview-card">
          <div className="locations-overview-header">
            <div className="locations-icon">
              <ShopOutlined />
            </div>
            <div>
              <Title level={4} style={{ margin: 0 }}>Locations Overview</Title>
              <Text type="secondary">All business locations at a glance</Text>
            </div>
          </div>

          <Row gutter={16} className="overview-stats">
            <Col span={4}>
              <div className="overview-stat">
                <ShopOutlined className="stat-icon" />
                <div>
                  <div className="stat-number">{client.totalLocations}</div>
                  <Text type="secondary">Total Locations</Text>
                </div>
              </div>
            </Col>
            <Col span={4}>
              <div className="overview-stat">
                <CarOutlined className="stat-icon" />
                <div>
                  <div className="stat-number">{client.totalVehicles}</div>
                  <Text type="secondary">Total Vehicles</Text>
                </div>
              </div>
            </Col>
            <Col span={4}>
              <div className="overview-stat">
                <RocketOutlined className="stat-icon" />
                <div>
                  <div className="stat-number">{client.activeCampaigns}</div>
                  <Text type="secondary">Active Campaigns</Text>
                </div>
              </div>
            </Col>
            <Col span={4}>
              <div className="overview-stat">
                <EnvironmentOutlined className="stat-icon" />
                <div>
                  <div className="stat-number">{client.routeMiles}</div>
                  <Text type="secondary">Route Miles</Text>
                </div>
              </div>
            </Col>
            <Col span={4}>
              <div className="overview-stat">
                <EyeOutlined className="stat-icon" />
                <div>
                  <div className="stat-number">{client.impressionsPerMile}</div>
                  <Text type="secondary">Impressions / Mile</Text>
                </div>
              </div>
            </Col>
            <Col span={4}>
              <div className="overview-stat">
                <div className="stat-icon">%</div>
                <div>
                  <div className="stat-number">{client.combinedCTR}</div>
                  <Text type="secondary">Combined CTR</Text>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      </div>

      {/* Your Plan Details */}
      <div className="plan-details-section">
        <div className="plan-header">
          <Title level={4} style={{ margin: 0 }}>Your Plan Details</Title>
          <Button type="link" onClick={() => navigate('/whitelabel/clients')}>
            Manage Plan
          </Button>
        </div>

        <Row gutter={16}>
          {/* RouteIQ */}
          <Col span={8}>
            <Card className="plan-card">
              <div className="plan-icon">
                <EnvironmentOutlined />
              </div>
              <Title level={5}>{client.plan.routeIQ.name}</Title>
              <Text type="secondary">{client.plan.routeIQ.description}</Text>

              <div className="plan-details">
                <div className="plan-row">
                  <Text>Vehicles</Text>
                  <Text strong>{client.plan.routeIQ.vehicles} units</Text>
                </div>
                <div className="plan-row">
                  <Text type="secondary">{client.plan.routeIQ.vehiclePrice}/vehicle</Text>
                  <Text strong>{client.plan.routeIQ.vehicleTotal}</Text>
                </div>
                <div className="plan-row">
                  <Text>Locations</Text>
                  <Text strong>{client.plan.routeIQ.locations} locations</Text>
                </div>
                <div className="plan-row">
                  <Text type="secondary">{client.plan.routeIQ.locationPrice}/location</Text>
                  <Text strong>{client.plan.routeIQ.locationTotal}</Text>
                </div>
                <div className="plan-row subtotal">
                  <Text strong>Subtotal</Text>
                  <Text strong>{client.plan.routeIQ.subtotal}</Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* ProximityIQ */}
          <Col span={8}>
            <Card className="plan-card">
              <div className="plan-icon proximity">
                <RocketOutlined />
              </div>
              <Title level={5}>{client.plan.proximityIQ.name}</Title>
              <Text type="secondary">{client.plan.proximityIQ.description}</Text>

              <div className="plan-details">
                <div className="plan-row">
                  <Text>SMS Credits</Text>
                  <Text strong>{client.plan.proximityIQ.smsCredits}</Text>
                </div>
                <div className="plan-row">
                  <div style={{ width: '100%', height: 6, background: '#e2e8f0', borderRadius: 3, marginTop: 4 }}>
                    <div style={{ width: '13%', height: '100%', background: '#3b82f6', borderRadius: 3 }}></div>
                  </div>
                </div>
                <div className="plan-row">
                  <Text>Email Credits</Text>
                  <Text strong>{client.plan.proximityIQ.emailCredits}</Text>
                </div>
                <div className="plan-row">
                  <div style={{ width: '100%', height: 6, background: '#e2e8f0', borderRadius: 3, marginTop: 4 }}>
                    <div style={{ width: '43%', height: '100%', background: '#3b82f6', borderRadius: 3 }}></div>
                  </div>
                </div>
                <div className="plan-row">
                  <Text type="secondary">Base Platform Fee (1 locations × $250)</Text>
                  <Text strong>{client.plan.proximityIQ.basePlatformFee}</Text>
                </div>
                <div className="plan-row">
                  <Text type="secondary">AI Growth Packages ($59/location)</Text>
                  <Text strong>{client.plan.proximityIQ.aiGrowthPackage}</Text>
                </div>
                <div className="plan-row subtotal">
                  <Text strong>Monthly Total</Text>
                  <Text strong>{client.plan.proximityIQ.monthlyTotal}</Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* Billing Summary */}
          <Col span={8}>
            <Card className="billing-summary-card">
              <Title level={5}>Billing Summary</Title>
              <Text type="secondary">Current billing cycle</Text>

              <div className="billing-details">
                <div className="billing-row">
                  <Text>RouteIQ</Text>
                  <Text strong>{client.plan.routeIQ.subtotal}</Text>
                </div>
                <div className="billing-row">
                  <Text>ProximityIQ</Text>
                  <Text strong>{client.plan.proximityIQ.monthlyTotal}</Text>
                </div>
                <div className="billing-total">
                  <Text strong>Platform Total</Text>
                  <Text strong className="total-amount">{client.plan.platformTotal}</Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ClientDashboard;
